import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import type { TContentBlockBase } from '@onesource/schemas';
import { type TFormSubmission, type TFormWithQuestions, fetchFormSubmission, getMediaUrl } from '@onesource/services';
import DOMPurify from 'dompurify';
import {
  MdCheckCircle,
  MdFileDownload,
  MdHourglassEmpty,
  MdOutlineAssignment,
  MdPlayArrow,
  MdRefresh,
} from 'react-icons/md';
import { downloadSubmissionPdf } from './submission-pdf';

interface FormReceptionProps {
  form: TFormWithQuestions;
  formBlock: TContentBlockBase;
  userSubmissions: TFormSubmission[] | undefined;
  isLoadingSubmissions: boolean;
  onStart: () => void;
  onResume: (submissionId: string) => void;
  isStarting: boolean;
  isResuming: boolean;
}

const formatDate = (value: string | null) => {
  if (!value) {
    return '';
  }
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const FormReception = ({
  form,
  formBlock,
  userSubmissions,
  isLoadingSubmissions,
  onStart,
  onResume,
  isStarting,
  isResuming,
}: FormReceptionProps) => {
  const theme = useTheme();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownloadPdf = async (submissionId: string) => {
    setDownloadingId(submissionId);
    try {
      const detail = await fetchFormSubmission(submissionId);
      await downloadSubmissionPdf(detail, form.title, form.questions ?? []);
    } catch (error) {
      console.error('Failed to download submission PDF:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const bannerSource = form.bannerUrl || formBlock.textBannerUrl;
  const bannerUrl = bannerSource ? getMediaUrl(bannerSource) : undefined;

  const submissions = [...(userSubmissions ?? [])].sort(
    (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
  );
  const inProgressSubmission = submissions.find(s => s.status === 'in_progress');
  const submittedCount = submissions.filter(s => s.status === 'submitted').length;
  const blockedByMultipleSubmissions = !form.allowMultipleSubmissions && submittedCount > 0;
  const questionCount = form.questions?.length ?? 0;

  const renderPrimaryAction = () => {
    if (isLoadingSubmissions) {
      return <CircularProgress size={28} />;
    }

    if (inProgressSubmission) {
      return (
        <Button
          startIcon={isResuming ? <CircularProgress size={18} color="inherit" /> : <MdRefresh size={20} />}
          onClick={() => onResume(inProgressSubmission.id)}
          disabled={isResuming || isStarting}
          variant="contained"
          sx={{
            bgcolor: '#ff4d6d',
            color: 'white',
            px: 3,
            py: 1.25,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { bgcolor: '#e63956' },
          }}
        >
          Resume Submission
        </Button>
      );
    }

    if (blockedByMultipleSubmissions) {
      return null;
    }

    return (
      <Button
        startIcon={isStarting ? <CircularProgress size={18} color="inherit" /> : <MdPlayArrow size={20} />}
        onClick={onStart}
        disabled={isStarting || isResuming || questionCount === 0}
        variant="contained"
        sx={{
          bgcolor: '#ff4d6d',
          color: 'white',
          px: 3,
          py: 1.25,
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 500,
          '&:hover': { bgcolor: '#e63956' },
          '&:disabled': { bgcolor: '#ccc', color: '#666' },
        }}
      >
        {submittedCount > 0 ? 'Start New Submission' : 'Start Form'}
      </Button>
    );
  };

  return (
    <Box maxWidth="800px" mx="auto" sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          borderRadius: '15px',
          overflow: 'hidden',
          bgcolor: 'white',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: '28.33%',
            background: bannerUrl
              ? `url(${bannerUrl}) center/cover no-repeat`
              : 'linear-gradient(135deg, #ff4d6d 0%, #ff8fa3 100%)',
          }}
        >
          {!bannerUrl && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MdOutlineAssignment size={72} color="rgba(255,255,255,0.9)" />
            </Box>
          )}
        </Box>

        <Box sx={{ p: { xs: 2.5, md: 4 } }}>
          <Typography
            variant="h4"
            sx={{ fontFamily: 'Georgia, serif', fontWeight: 500, fontSize: { xs: '1.5rem', md: '2rem' }, mb: 1.5 }}
          >
            {form.title}
          </Typography>

          {form.description && (
            <Typography
              component="div"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.6,
                fontSize: '0.95rem',
                mb: 3,
                '& p': { m: 0, mb: 1 },
                '& ul, & ol': { pl: 3, m: 0, mb: 1 },
              }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.description) }}
            />
          )}

          {questionCount === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              This form has no questions yet.
            </Alert>
          )}

          {blockedByMultipleSubmissions && !inProgressSubmission && (
            <Alert severity="info" sx={{ mb: 3 }}>
              You have already submitted this form. Multiple submissions are not allowed.
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>{renderPrimaryAction()}</Box>
        </Box>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1.1rem' }}>
          Your Submissions
        </Typography>

        {isLoadingSubmissions ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : submissions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            You have not submitted this form yet.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {submissions.map(submission => {
              const isInProgress = submission.status === 'in_progress';
              return (
                <Box
                  key={submission.id}
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: isInProgress ? '#ffd1da' : theme.palette.divider,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                    {isInProgress ? (
                      <MdHourglassEmpty size={22} color="#ff4d6d" />
                    ) : (
                      <MdCheckCircle size={22} color="#4caf50" />
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Submission #{submission.submissionNumber}
                        <Chip
                          size="small"
                          label={isInProgress ? 'In Progress' : 'Submitted'}
                          sx={{
                            ml: 1,
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: isInProgress ? '#fff0f3' : '#e8f5e9',
                            color: isInProgress ? '#ff4d6d' : '#2e7d32',
                          }}
                        />
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Started {formatDate(submission.startedAt)}
                        {submission.submittedAt && ` · Submitted ${formatDate(submission.submittedAt)}`}
                      </Typography>
                    </Box>
                  </Box>

                  {isInProgress ? (
                    <Button
                      size="small"
                      onClick={() => onResume(submission.id)}
                      disabled={isResuming || isStarting}
                      sx={{
                        bgcolor: '#ff4d6d',
                        color: 'white',
                        px: 2,
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#e63956' },
                      }}
                    >
                      Resume
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        downloadingId === submission.id ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <MdFileDownload size={16} />
                        )
                      }
                      onClick={() => handleDownloadPdf(submission.id)}
                      disabled={downloadingId === submission.id}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#ff4d6d',
                        color: '#ff4d6d',
                        '&:hover': { borderColor: '#e63956', bgcolor: 'rgba(255,77,109,0.06)' },
                      }}
                    >
                      Download PDF
                    </Button>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <Divider sx={{ mt: 4 }} />
    </Box>
  );
};
